import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  contracts: 'Contratos',
  new: 'Novo',
  edit: 'Editar',
  search: 'Busca',
  'my-approvals': 'Minhas Aprovacoes',
  calendar: 'Calendario',
  installments: 'Parcelas',
  obligations: 'Obrigacoes',
  'obligation-templates': 'Templates de Obrigacoes',
  users: 'Usuarios',
  members: 'Membros',
  teams: 'Equipes',
  templates: 'Templates',
  clauses: 'Clausulas',
  'clause-templates': 'Biblioteca de Clausulas',
  workflows: 'Workflows',
  instances: 'Instancias',
  reports: 'Relatorios',
  'custom-reports': 'Relatorios Personalizados',
  settings: 'Configuracoes',
  notifications: 'Notificacoes',
  'email-templates': 'Templates de E-mail',
  organizations: 'Organizacoes',
  invitations: 'Convites',
  'user-guide': 'Manual do Usuario',
  'audit-log': 'Historico',
  'system-check': 'Diagnostico',
  'permissions-debug': 'Debug de Permissoes',
  'user-approvals': 'Aprovacoes',
  'notification-settings': 'Config. Notificacoes',
  pending: 'Pendentes',
};

// UUIDs pattern to detect dynamic segments
const isUUID = (segment: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);

interface BreadcrumbProps {
  className?: string;
  customLabels?: Record<string, string>;
}

export function Breadcrumb({ className, customLabels }: BreadcrumbProps) {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  if (pathSegments.length <= 1 && pathSegments[0] === 'dashboard') {
    return null; // Don't show breadcrumbs on dashboard
  }

  const crumbs = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    const isLast = index === pathSegments.length - 1;

    let label = customLabels?.[segment] || routeLabels[segment];
    if (!label) {
      if (isUUID(segment)) {
        label = 'Detalhes';
      } else {
        label = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      }
    }

    return { path, label, isLast };
  });

  return (
    <nav className={cn('flex items-center gap-1.5 text-sm', className)}>
      <Link
        to="/dashboard"
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        <Home className="w-3.5 h-3.5" />
      </Link>
      {crumbs.map((crumb) => (
        <span key={crumb.path} className="flex items-center gap-1.5">
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
          {crumb.isLast ? (
            <span className="text-foreground font-medium">{crumb.label}</span>
          ) : (
            <Link
              to={crumb.path}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {crumb.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  );
}
